<script>
  import { tick } from 'svelte'
  import { useForm, router, page } from '@inertiajs/svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import SearchInput from '../../components/search_input.svelte'
  import FilterSelect from '../../components/filter_select.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import Paginator from '../../components/paginator.svelte'
  import Drawer from '../../components/drawer.svelte'
  import FilterBar from '../../components/filter_bar.svelte'
  import EmptyState from '../../components/empty_state.svelte'
  import { confirm } from '../../components/confirm.js'
  import ClickableImage from '../../components/lightbox/clickable_image.svelte'
  import ImageUploadDrawer from '../../components/image_upload_drawer.svelte'
  import { Button } from '../../components/ui'

  let { images = [], pagy = null, total_count = null, filters = {} } = $props()

  let searchValue = $state(filters.search || '')
  let uploadOpen = $state(false)
  let editOpen = $state(false)
  let editingImage = $state(null)

  // Each field is checked live against media#update (Precognition) before Save.
  const editForm = useForm({ image: { title: '', all_tags: '', place_id: '' } }).withPrecognition(
    'patch',
    () => `/media/${editingImage?.id}`
  )

  // Gallery items for the lightbox prev/next, built from whatever page of images is loaded.
  const galleryItems = $derived(images.map((image) => ({ type: 'image', url: image.url, title: image.title })))

  const openEdit = (image) => {
    editingImage = image
    $editForm.image = { title: image.title || '', all_tags: (image.all_tags || []).join(', '), place_id: image.place_id || '' }
    $editForm.clearErrors()
    editOpen = true
  }

  const submitEdit = (event) => {
    event.preventDefault()
    $editForm.patch(`/media/${editingImage.id}`, {
      preserveScroll: true,
      onSuccess: () => {
        editOpen = false
      }
    })
  }

  const onDelete = async (image) => {
    const ok = await confirm({
      title: 'Confirm Archive',
      variant: 'error',
      body:
        "Archiving an image will remove it from any campaigns, feeds, places, or adventures it's part of. This action is permanent unless reversed by a developer. Are you sure?",
      confirmLabel: 'Archive'
    })
    if (!ok) return

    router.delete(`/media/${image.id}`, { preserveScroll: true })
  }

  // Places are narrowed to the sidebar's College Town when one is selected.
  const placeSourceParams = $derived({ area_id: $page.props.current_area_id })
</script>

<Layout>
  <PageHeader title="Media Library" subtitle="Uploaded images used across campaigns, places, feeds, and adventures" count={pagy.count} total={total_count}>
    {#snippet actions()}
      <Button variant="primary" size="sm" onclick={() => (uploadOpen = true)}>
        Upload Images…
      </Button>
    {/snippet}
  </PageHeader>

  <FilterBar>
    {#snippet start()}
      <SearchInput bind:value={searchValue} placeholder="Search images…" />

      <FilterSelect
        label="Place"
        param="place_id"
        value={filters.place_id || ''}
        placeholder="All places"
        source="places"
        sourceParams={placeSourceParams}
      />
    {/snippet}
  </FilterBar>

  {#if images.length === 0}
    <EmptyState icon="media" title="No images found." />
  {:else}
    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {#each images as image (image.id)}
        <div class="card bg-base-100 border border-base-300 overflow-hidden">
          <ClickableImage
            url={image.url}
            title={image.title}
            items={galleryItems}
            thumbClass="aspect-square w-full object-contain bg-base-200"
          />
          <div class="p-2 flex flex-col grow">
            <p class="text-sm font-medium truncate" title={image.title}>{image.title || 'Untitled'}</p>
            {#if image.place}
              <p class="text-xs text-base-content/60 truncate">
                {image.place.label}
              </p>
            {/if}
            <div class="flex justify-end gap-1 mt-auto pt-2">
              <Button variant="ghost" size="xs" square icon="edit" aria-label="Edit" onclick={() => openEdit(image)} />
              <Button variant="ghost" size="xs" square icon="delete" aria-label="Delete" class="text-error" onclick={() => onDelete(image)} />
            </div>
          </div>
        </div>
      {/each}
    </div>

    {#if pagy}
      <Paginator page={pagy.page} pages={pagy.pages} count={pagy.count} />
    {/if}
  {/if}
</Layout>

<!-- The upload redirects back here; asking for images/pagy too refreshes the grid. -->
<ImageUploadDrawer bind:open={uploadOpen} placeId={filters.place_id} only={['images', 'pagy', 'flash']} />

<Drawer bind:open={editOpen} title="Edit Image">
  {#if editingImage}
    <div class="mb-4">
      <ClickableImage url={editingImage.url} title={editingImage.title || ''} buttonClass="block w-full" thumbClass="w-full h-48 object-contain bg-base-200 rounded-box" />
    </div>

    <form novalidate onsubmit={submitEdit} class="space-y-4">
      <SelectField
        bind:value={$editForm.image.place_id}
        label="Place"
        name="place_id"
        source="places"
        placeholder="No place"
        onchange={() => tick().then(() => $editForm.validate('image.place_id'))}
        error={$editForm.errors['image.place_id']}
      />
      <div onchange={() => $editForm.validate('image.title')}>
        <TextField bind:value={$editForm.image.title} label="Image Title" name="title" error={$editForm.errors['image.title']} />
      </div>
      <div onchange={() => $editForm.validate('image.all_tags')}>
        <TextField
          bind:value={$editForm.image.all_tags}
          label="Image Tags"
          name="all_tags"
          help="Comma separated"
          error={$editForm.errors['image.all_tags']}
        />
      </div>

      <SubmitButton processing={$editForm.processing} label="Save" />
    </form>
  {/if}
</Drawer>
